import React from "react";
import { Breadcrumb } from "components/ui/BootstrapCompat";
import { useQuery } from "react-query";
import useLocalStorage from "helpers/useLocalStorage";
import {
  userKycGetMe,
  userKycGetStatus,
  userGetMe,
  userWalletGetMe,
} from "services/user.service";
import { WITHDRAW_FEE_AMOUNT } from "../withdraw.types";
import WithdrawConfirmModal from "./WithdrawConfirmModal";
import WithdrawHistoryTable from "./WithdrawHistoryTable";
import WithdrawMonthlyChartCard from "./WithdrawMonthlyChartCard";
import WithdrawRequirementsAlert from "./WithdrawRequirementsAlert";
import WithdrawSummaryCard from "./WithdrawSummaryCard";

export default function WithdrawScreen() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");

  const { data: walletData, isLoading: isLoadingWallet } = useQuery(
    ["wallet-me", businessId],
    async () => {
      if (!token) return null;
      return await userWalletGetMe({ token, businessId });
    },
    { enabled: !!token },
  );

  const { data: kycData, isLoading: isLoadingKyc } = useQuery(
    ["user-kyc-me", businessId],
    async () => {
      if (!token || !businessId) return null;
      return await userKycGetMe({ token, businessId });
    },
    { enabled: !!token && !!businessId },
  );

  const { data: kycStatusData } = useQuery(
    ["user-kyc-status", businessId],
    async () => {
      if (!token || !businessId) return null;
      return await userKycGetStatus({ token, businessId });
    },
    { enabled: !!token && !!businessId, retry: false },
  );

  const { data: authMeData } = useQuery(
    ["auth-me", businessId],
    async () => {
      if (!token) return null;
      return await userGetMe({ token, businessId });
    },
    { enabled: !!token, retry: false },
  );

  const walletPayload = (walletData as any)?.data ?? walletData;
  const availableAmount = Number(
    walletPayload?.amount ?? walletPayload?.balance ?? 0,
  );
  const currencyLabel = String(walletPayload?.currency || "ກີບ");
  const feeAmount = availableAmount > 0 ? WITHDRAW_FEE_AMOUNT : 0;
  const netAmount = Math.max(availableAmount - feeAmount, 0);

  const kycPayload = (kycData as any)?.data ?? kycData ?? {};
  const bankName = String(kycPayload?.bankName || "").trim();
  const accountName = String(kycPayload?.bankAccountName || "").trim();
  const accountNumber = String(kycPayload?.bankAccountNo || "").trim();

  const kycStatusRaw =
    (kycStatusData as any)?.data?.status ||
    (typeof (kycStatusData as any)?.data === "string"
      ? (kycStatusData as any).data
      : "") ||
    (kycStatusData as any)?.status ||
    (kycPayload as any)?.status ||
    "draft";
  const kycStatus = String(kycStatusRaw || "draft")
    .trim()
    .toLowerCase();

  const kycStatusLabel =
    kycStatus === "approved"
      ? "ອະນຸມັດແລ້ວ"
      : kycStatus === "pending"
        ? "ລໍຖ້າກວດສອບ"
        : kycStatus === "draft"
          ? "ຍັງບໍ່ໄດ້ສົ່ງ"
          : "ຖືກປະຕິເສດ";

  const canWithdraw =
    netAmount > 0 &&
    kycStatus === "approved" &&
    !!bankName &&
    !!accountName &&
    !!accountNumber;

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const openWithdrawModal = () => {
    setIsModalOpen(true);
  };

  const closeWithdrawModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ການຕັ້ງຄ່າ</Breadcrumb.Item>
        <Breadcrumb.Item active>ຖອນເງິນ</Breadcrumb.Item>
      </Breadcrumb>

      <div className="grid gap-3 lg:grid-cols-[1fr_1fr] mb-3">
        <WithdrawSummaryCard
          isLoadingWallet={isLoadingWallet}
          isLoadingKyc={isLoadingKyc}
          canWithdraw={canWithdraw}
          availableAmount={availableAmount}
          feeAmount={feeAmount}
          netAmount={netAmount}
          currencyLabel={currencyLabel}
          kycStatusLabel={kycStatusLabel}
          bankName={bankName}
          accountName={accountName}
          accountNumber={accountNumber}
          onWithdrawClick={openWithdrawModal}
        />

        <WithdrawMonthlyChartCard />
      </div>

      <WithdrawRequirementsAlert
        show={!canWithdraw}
        availableAmount={availableAmount}
        currencyLabel={currencyLabel}
        kycStatus={kycStatus}
        bankName={bankName}
        accountName={accountName}
        accountNumber={accountNumber}
      />

      <WithdrawHistoryTable currencyLabel={currencyLabel} />

      <WithdrawConfirmModal show={isModalOpen} onClose={closeWithdrawModal} />
    </div>
  );
}
